import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Calendar, 
  Clock, 
  User, 
  Phone,
  Scissors,
  CheckCircle,
  ArrowRight,
  Loader2,
  Gift,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';

// "HH:MM" → minutes since midnight
const _hhmm = (t) => {
  if (!t || typeof t !== 'string') return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

// Format Date → "YYYY-MM-DD" using local time (avoids UTC shift).
const _toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Booking window: today .. upcoming Sunday of the current week (Mon..Sun).
const _bookingWindow = () => {
  const today = new Date();
  const dow = today.getDay(); // 0=Sun..6=Sat
  // Days until Sunday inclusive (treating Sunday as end of week)
  const daysUntilSunday = dow === 0 ? 0 : 7 - dow;
  const sunday = new Date(today);
  sunday.setDate(today.getDate() + daysUntilSunday);
  return { min: _toYMD(today), max: _toYMD(sunday) };
};

const _formatDMY = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};

export default function AgendarCitaPage() {
  const [searchParams] = useSearchParams();
  const businessSlug = searchParams.get('negocio') || 'barrabas';
  const preselectedClientId = searchParams.get('cliente');

  return <AgendarCitaForm businessSlug={businessSlug} preselectedClientId={preselectedClientId} />;
}

function AgendarCitaForm({ businessSlug, preselectedClientId }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [clientData, setClientData] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  const [formData, setFormData] = useState({
    phone: '',
    service_id: '',
    barber_id: '',
    date: '',
    time: ''
  });

  // Availability for selected barber + date
  const [availability, setAvailability] = useState(null); // { open, close, closed_reason, occupied: [{start,end}] }
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  const isJLSaloon = false;

  // Load business data
  const loadBusinessData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/public/business/${businessSlug}`);
      setBusiness(response.data.business);
      setServices(response.data.services || []);
      setBarbers(response.data.barbers || []);
    } catch (error) {
      console.error('Error loading business:', error);
      toast.error('No se pudo cargar la información del negocio');
    } finally {
      setLoading(false);
    }
  }, [businessSlug]);

  useEffect(() => {
    loadBusinessData();
  }, [loadBusinessData]);

  // Fetch availability for the given barber + date and clear an invalid time.
  const fetchAvailability = useCallback(async (barberId, date) => {
    if (!barberId || !date) {
      setAvailability(null);
      return;
    }
    setLoadingAvailability(true);
    try {
      const qs = new URLSearchParams({
        business_slug: businessSlug,
        barber_id: barberId,
        date,
      }).toString();
      const { data } = await api.get(`/public/availability?${qs}`);
      setAvailability(data);
    } catch (e) {
      setAvailability({ open: null, close: null,
        closed_reason: 'No se pudo consultar disponibilidad', occupied: [] });
    } finally {
      setLoadingAvailability(false);
    }
  }, [businessSlug]);

  // Check if client exists by phone
  const checkClient = async () => {
    if (formData.phone.length !== 10) {
      toast.error('El teléfono debe tener 10 dígitos');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/public/check-client', {
        phone: formData.phone,
        business_slug: businessSlug
      });
      
      if (response.data.found) {
        setClientData(response.data.client);
        toast.success(`¡Hola ${response.data.client.first_name || 'de nuevo'}!`);
      } else {
        setClientData({ phone: formData.phone, is_new: true });
        toast.info('Número nuevo. Continúa para crear tu cita.');
      }
      setStep(2);
    } catch (error) {
      toast.error('Error al verificar teléfono');
    } finally {
      setSubmitting(false);
    }
  };

  // Generate time slots in 15-minute increments within the effective window.
  const generateTimeSlots = () => {
    const slots = [];
    const fromMin = availability?.open ? _hhmm(availability.open) : 9 * 60;
    const toMin = availability?.close ? _hhmm(availability.close) : 20 * 60;
    for (let m = fromMin; m < toMin; m += 15) {
      slots.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
    }
    return slots;
  };

  // Check if a given start slot is bookable for the selected service.
  // Returns null when bookable, or a string reason when not.
  const slotConflictReason = (slot) => {
    if (!availability) return null;
    if (!availability.open || !availability.close) return availability.closed_reason || 'Día no disponible';
    const endTime = calculateEndTime(slot);
    if (!endTime) return null;
    const s = _hhmm(slot);
    const e = _hhmm(endTime);
    const open = _hhmm(availability.open);
    const close = _hhmm(availability.close);
    if (s < open || e > close) return 'Fuera de horario';
    const conflict = (availability.occupied || []).find((o) => {
      const os = _hhmm(o.start);
      const oe = _hhmm(o.end);
      return s < oe && e > os;
    });
    if (conflict) return `Ocupado ${conflict.start}-${conflict.end}`;
    return null;
  };

  // Calculate end time
  const calculateEndTime = (startTime) => {
    if (!startTime || !formData.service_id) return '';
    const service = services.find(s => s.id === formData.service_id);
    const duration = service?.duration_minutes || 30;
    
    const [hours, minutes] = startTime.split(':').map(Number);
    const endDate = new Date(2000, 0, 1, hours, minutes + duration);
    return `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
  };

  // Submit booking
  const handleSubmit = async () => {
    if (!formData.service_id || !formData.barber_id || !formData.date || !formData.time) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/public/book-appointment', {
        phone: formData.phone,
        first_name: clientData?.first_name || null,
        service_id: formData.service_id,
        barber_id: formData.barber_id,
        date: formData.date,
        start_time: formData.time,
        end_time: calculateEndTime(formData.time),
        business_slug: businessSlug
      });
      
      setBookingSuccess(true);
      toast.success('¡Cita agendada exitosamente!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al agendar cita';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatPhone = (value) => {
    return value.replace(/\D/g, '').slice(0, 10);
  };

  const selectedService = services.find(s => s.id === formData.service_id);
  const selectedBarber = barbers.find(b => b.id === formData.barber_id);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isJLSaloon ? 'bg-gradient-to-br from-purple-50 to-pink-50' : 'bg-slate-50'}`}>
        <Loader2 className={`w-8 h-8 animate-spin ${isJLSaloon ? 'text-purple-600' : 'text-primary'}`} />
      </div>
    );
  }

  // Success screen
  if (bookingSuccess) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isJLSaloon ? 'bg-gradient-to-br from-purple-50 to-pink-50' : 'bg-slate-50'}`}>
        <Card className={`w-full max-w-md text-center ${isJLSaloon ? 'border-purple-100 shadow-xl bg-white' : 'border-slate-200 bg-white'}`}>
          <CardContent className="pt-8 pb-8">
            <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${isJLSaloon ? 'bg-green-100' : 'bg-emerald-500/20'}`}>
              <CheckCircle className={`w-10 h-10 ${isJLSaloon ? 'text-green-600' : 'text-emerald-400'}`} />
            </div>
            <h2 className={`text-2xl font-bold mb-2 ${isJLSaloon ? 'text-gray-900' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope' }}>
              ¡Cita Confirmada!
            </h2>
            <p className={`mb-6 ${isJLSaloon ? 'text-gray-600' : 'text-slate-500'}`}>
              Tu cita ha sido agendada exitosamente
            </p>
            
            <div className={`p-4 rounded-lg mb-6 text-left ${isJLSaloon ? 'bg-purple-50' : 'bg-slate-100'}`}>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className={isJLSaloon ? 'text-gray-500' : 'text-slate-500'}>Servicio:</span>
                  <span className={`font-medium ${isJLSaloon ? 'text-gray-900' : 'text-slate-900'}`}>{selectedService?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isJLSaloon ? 'text-gray-500' : 'text-slate-500'}>Profesional:</span>
                  <span className={`font-medium ${isJLSaloon ? 'text-gray-900' : 'text-slate-900'}`}>{selectedBarber?.display_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isJLSaloon ? 'text-gray-500' : 'text-slate-500'}>Fecha:</span>
                  <span className={`font-medium ${isJLSaloon ? 'text-gray-900' : 'text-slate-900'}`}>{formData.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isJLSaloon ? 'text-gray-500' : 'text-slate-500'}>Hora:</span>
                  <span className={`font-medium ${isJLSaloon ? 'text-gray-900' : 'text-slate-900'}`}>{formData.time}</span>
                </div>
              </div>
            </div>

            {!clientData?.loyalty_points && clientData?.is_new && (
              <div className={`p-4 rounded-lg mb-6 ${isJLSaloon ? 'bg-yellow-50 border border-yellow-200' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                <div className="flex items-center gap-3">
                  <Gift className={`w-5 h-5 ${isJLSaloon ? 'text-yellow-600' : 'text-yellow-400'}`} />
                  <div className="text-left">
                    <p className={`text-sm font-medium ${isJLSaloon ? 'text-yellow-800' : 'text-yellow-400'}`}>
                      ¡Únete al programa de lealtad!
                    </p>
                    <p className={`text-xs ${isJLSaloon ? 'text-yellow-700' : 'text-yellow-500'}`}>
                      Acumula puntos y recibe beneficios
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button 
              onClick={() => {
                setBookingSuccess(false);
                setStep(1);
                setFormData({ phone: '', service_id: '', barber_id: '', date: '', time: '' });
                setClientData(null);
              }}
              className={`w-full ${isJLSaloon ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-primary text-white hover:bg-primary/90'}`}
            >
              Agendar otra cita
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-4 ${isJLSaloon ? 'bg-gradient-to-br from-purple-50 to-pink-50' : 'bg-slate-50'}`}>
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8 pt-8">
          {business?.logo_url && !business.logo_url.toLowerCase().endsWith('.pdf') && (
            <img 
              src={business.logo_url} 
              alt={business.name} 
              className="h-16 mx-auto mb-4 object-contain"
            />
          )}
          <h1 className={`text-3xl font-bold ${isJLSaloon ? 'text-gray-900' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope' }}>
            {business?.name || 'Agendar Cita'}
          </h1>
          <p className={`mt-2 ${isJLSaloon ? 'text-gray-600' : 'text-slate-500'}`}>
            Reserva tu cita en minutos
          </p>
        </div>

        {/* Progress steps */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? (isJLSaloon ? 'text-purple-600' : 'text-slate-900') : (isJLSaloon ? 'text-gray-400' : 'text-slate-500')}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? (isJLSaloon ? 'bg-purple-600 text-white' : 'bg-primary text-white') : (isJLSaloon ? 'bg-gray-200' : 'bg-slate-100')}`}>
              1
            </div>
            <span className="text-sm hidden sm:inline">Teléfono</span>
          </div>
          <div className={`w-8 h-px ${step >= 2 ? (isJLSaloon ? 'bg-purple-600' : 'bg-primary') : (isJLSaloon ? 'bg-gray-300' : 'bg-slate-200')}`} />
          <div className={`flex items-center gap-2 ${step >= 2 ? (isJLSaloon ? 'text-purple-600' : 'text-slate-900') : (isJLSaloon ? 'text-gray-400' : 'text-slate-500')}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? (isJLSaloon ? 'bg-purple-600 text-white' : 'bg-primary text-white') : (isJLSaloon ? 'bg-gray-200' : 'bg-slate-100')}`}>
              2
            </div>
            <span className="text-sm hidden sm:inline">Detalles</span>
          </div>
        </div>

        {/* Step 1: Phone */}
        {step === 1 && (
          <Card className={isJLSaloon ? 'border-purple-100 shadow-xl bg-white' : 'border-slate-200 bg-white'}>
            <CardHeader>
              <CardTitle className={isJLSaloon ? 'text-gray-900' : 'text-slate-900'} style={{ fontFamily: 'Manrope' }}>
                Ingresa tu teléfono
              </CardTitle>
              <CardDescription className={isJLSaloon ? 'text-gray-600' : 'text-slate-500'}>
                Con tu número identificamos tus citas y puntos de lealtad
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className={isJLSaloon ? 'text-gray-700' : 'text-slate-700'}>
                  <Phone className="w-4 h-4 inline mr-2" />
                  Teléfono (10 dígitos)
                </Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                  placeholder="5512345678"
                  className={`text-xl tracking-wider text-center ${isJLSaloon ? 'border-gray-300 focus:border-purple-500' : 'bg-slate-100 border-slate-200'}`}
                  maxLength={10}
                  data-testid="input-phone"
                />
                <p className={`text-xs text-center ${isJLSaloon ? 'text-gray-500' : 'text-slate-500'}`}>
                  {formData.phone.length}/10 dígitos
                </p>
              </div>

              <Button 
                onClick={checkClient}
                disabled={formData.phone.length !== 10 || submitting}
                className={`w-full ${isJLSaloon ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-primary text-white hover:bg-primary/90'}`}
                data-testid="continue-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Continuar
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <div className="text-center">
                <Link 
                  to={`/registro?negocio=${businessSlug}`}
                  className={`text-sm ${isJLSaloon ? 'text-purple-600 hover:text-purple-700' : 'text-slate-500 hover:text-slate-900'}`}
                >
                  ¿Primera vez? Regístrate para el programa de lealtad
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Booking Details */}
        {step === 2 && (
          <Card className={isJLSaloon ? 'border-purple-100 shadow-xl bg-white' : 'border-slate-200 bg-white'}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className={isJLSaloon ? 'text-gray-900' : 'text-slate-900'} style={{ fontFamily: 'Manrope' }}>
                    Detalles de la cita
                  </CardTitle>
                  {clientData && !clientData.is_new && (
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className={isJLSaloon ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-800'}>
                        <Star className="w-3 h-3 mr-1" />
                        {clientData.loyalty_points || 0} puntos
                      </Badge>
                    </div>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setStep(1)}
                  className={isJLSaloon ? 'text-gray-600' : 'text-slate-500'}
                >
                  Cambiar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Service */}
              <div className="space-y-2">
                <Label className={isJLSaloon ? 'text-gray-700' : 'text-slate-700'}>
                  <Scissors className="w-4 h-4 inline mr-2" />
                  Servicio
                </Label>
                <Select 
                  value={formData.service_id} 
                  onValueChange={(value) => setFormData({ ...formData, service_id: value })}
                >
                  <SelectTrigger className={isJLSaloon ? 'border-gray-300' : 'bg-slate-100 border-slate-200'}>
                    <SelectValue placeholder="Seleccionar servicio" />
                  </SelectTrigger>
                  <SelectContent className={isJLSaloon ? '' : 'bg-white border-slate-200'}>
                    <ScrollArea className="h-48">
                      {services.map(service => (
                        <SelectItem key={service.id} value={service.id}>
                          <div className="flex justify-between items-center w-full">
                            <span>{service.name}</span>
                            <span className={`ml-4 ${isJLSaloon ? 'text-gray-500' : 'text-slate-500'}`}>
                              {formatCurrency(service.price)} • {service.duration_minutes}min
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>
              </div>

              {/* Barber */}
              <div className="space-y-2">
                <Label className={isJLSaloon ? 'text-gray-700' : 'text-slate-700'}>
                  <User className="w-4 h-4 inline mr-2" />
                  Profesional
                </Label>
                <Select 
                  value={formData.barber_id} 
                  onValueChange={(value) => {
                    setFormData({ ...formData, barber_id: value, time: '' });
                    fetchAvailability(value, formData.date);
                  }}
                >
                  <SelectTrigger className={isJLSaloon ? 'border-gray-300' : 'bg-slate-100 border-slate-200'}>
                    <SelectValue placeholder="Seleccionar profesional" />
                  </SelectTrigger>
                  <SelectContent className={isJLSaloon ? '' : 'bg-white border-slate-200'}>
                    {barbers.map(barber => (
                      <SelectItem key={barber.id} value={barber.id}>
                        {barber.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <Label className={isJLSaloon ? 'text-gray-700' : 'text-slate-700'}>
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Fecha
                </Label>
                {(() => {
                  const { min, max } = _bookingWindow();
                  return (
                    <>
                      <Input
                        type="date"
                        value={formData.date}
                        min={min}
                        max={max}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v && (v < min || v > max)) {
                            toast.error(`Solo puedes agendar entre hoy y el domingo ${_formatDMY(max)}.`);
                            return;
                          }
                          setFormData({ ...formData, date: v, time: '' });
                          fetchAvailability(formData.barber_id, v);
                        }}
                        className={isJLSaloon ? 'border-gray-300' : 'bg-slate-100 border-slate-200'}
                        data-testid="input-date"
                      />
                      <p className={`text-xs ${isJLSaloon ? 'text-gray-500' : 'text-slate-500'}`}>
                        Puedes agendar solo dentro de esta semana (hasta el domingo <span className="font-medium">{_formatDMY(max)}</span>).
                      </p>
                    </>
                  );
                })()}
              </div>

              {/* Time */}
              <div className="space-y-2">
                <Label className={isJLSaloon ? 'text-gray-700' : 'text-slate-700'}>
                  <Clock className="w-4 h-4 inline mr-2" />
                  Hora
                  {loadingAvailability && (
                    <Loader2 className="w-3 h-3 ml-2 inline-block animate-spin text-slate-400" />
                  )}
                </Label>
                {availability?.closed_reason && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    {availability.closed_reason}
                  </p>
                )}
                {availability?.open && availability?.close && (
                  <p className="text-xs text-slate-500">
                    Horario disponible: <span className="font-medium">{availability.open}</span> – <span className="font-medium">{availability.close}</span>
                    {availability.occupied?.length > 0 && (
                      <span className="ml-2">· {availability.occupied.length} cita(s) ya reservada(s)</span>
                    )}
                  </p>
                )}
                <Select
                  value={formData.time}
                  onValueChange={(value) => setFormData({ ...formData, time: value })}
                  disabled={!formData.barber_id || !formData.date || !formData.service_id || !availability?.open}
                >
                  <SelectTrigger
                    className={isJLSaloon ? 'border-gray-300' : 'bg-slate-100 border-slate-200'}
                    data-testid="select-time-trigger"
                  >
                    <SelectValue placeholder={!formData.service_id ? 'Primero elige servicio, profesional y fecha' : 'Seleccionar hora'} />
                  </SelectTrigger>
                  <SelectContent className={isJLSaloon ? '' : 'bg-white border-slate-200'}>
                    <ScrollArea className="h-48">
                      {generateTimeSlots().map(slot => {
                        const reason = slotConflictReason(slot);
                        return (
                          <SelectItem
                            key={slot}
                            value={slot}
                            disabled={!!reason}
                            data-testid={`time-slot-${slot}${reason ? '-disabled' : ''}`}
                          >
                            <span className="flex items-center justify-between w-full gap-3">
                              <span className={reason ? 'line-through text-slate-400' : ''}>{slot}</span>
                              {reason && (
                                <span className="text-[10px] text-red-500 ml-2">{reason}</span>
                              )}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </ScrollArea>
                  </SelectContent>
                </Select>
              </div>

              {/* Summary */}
              {selectedService && (
                <div className={`p-4 rounded-lg ${isJLSaloon ? 'bg-purple-50' : 'bg-slate-100'}`}>
                  <div className="flex justify-between items-center">
                    <span className={isJLSaloon ? 'text-gray-600' : 'text-slate-500'}>Total:</span>
                    <span className={`text-xl font-bold ${isJLSaloon ? 'text-purple-700' : 'text-slate-900'}`}>
                      {formatCurrency(selectedService.price)}
                    </span>
                  </div>
                </div>
              )}

              <Button 
                onClick={handleSubmit}
                disabled={!formData.service_id || !formData.barber_id || !formData.date || !formData.time || submitting}
                className={`w-full ${isJLSaloon ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-primary text-white hover:bg-primary/90'}`}
                data-testid="book-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Confirmar cita
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
